import React, { useState, useEffect } from 'react';
import {
  useWebSockets,
  useVehiclesService,
  useAuthService,
  useVehicleNotifications,
  usePlanStatus,
} from '../../hooks';
import useUserLocation from '../../hooks/useUserLocation';
import { capitalize, loadScriptByURL } from '../../utils';
import { MapPage } from '../../pages';

const recaptchaKey = '6LcgYzUeAAAAAMzXJ7ddC57PNtA0k-stcLituAZE';
const NO_POSITION_GRACE_MS = 5000;

// Igual que la app y trakku-admin: sin IMEI la moto no tiene dispositivo GPS.
const hasGpsDevice = (vehicle) => Boolean(vehicle?.imei);
const initialRegion = {
  latitude: 4.3641759,
  longitude: -73.3833968,
  latitudeDelta: 1,
  longitudeDelta: 1,
};
function MapContainer() {
  const [urlShare, setUrlShare] = useState('');
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [selectedVehicleData, setSelectedVehicleData] = useState(null);
  const [positionTimedOut, setPositionTimedOut] = useState(false);
  const [view, setView] = useState({
    detail: false,
    clinic: null,
    center: {
      lat: initialRegion.latitude,
      lng: initialRegion.longitude,
    },
    zoomLevel: 6.5,
  });
  const { logout } = useAuthService();
  const {
    vehiclesList, turnOffVehicle, turnONVehicle, getUrlService, 
  } = useVehiclesService();
  const {
    permission: notificationPermission,
    enableNotifications,
    handleVehicleEvent,
    history: notificationHistory,
    unreadCount,
    lastReadAt: notificationsReadAt,
    refreshHistory,
    markAsRead,
  } = useVehicleNotifications(vehicles);
  const { useSocket, position } = useWebSockets({ onEvent: handleVehicleEvent });

  // Moto sin GPS, o con GPS pero sin posición después de unos segundos: se muestra al usuario.
  const selectedVehicleInfo = vehicles.find((vehicle) => vehicle.label === selectedVehicle);
  const hasPosition = Boolean(selectedVehicleData?.length);
  const showUserLocation = Boolean(selectedVehicleInfo)
    && !hasPosition
    && (!hasGpsDevice(selectedVehicleInfo) || positionTimedOut);
  const { userLocation, error: locationError } = useUserLocation({ enabled: showUserLocation });
  const plan = usePlanStatus(selectedVehicleInfo, hasGpsDevice(selectedVehicleInfo) || hasPosition);

  useEffect(() => {
    setPositionTimedOut(false);
    if (!selectedVehicle) return undefined;
    const timeoutId = setTimeout(() => setPositionTimedOut(true), NO_POSITION_GRACE_MS);
    return () => clearTimeout(timeoutId);
  }, [selectedVehicle]);

  useEffect(() => {
    (async () => {
      const vehiculos = await vehiclesList();
      if (Array.isArray(vehiculos)) {
        const veh = vehiculos.map((vehicle) => ({
          ...vehicle,
          ...vehicle.vehiculo,
          label: `${capitalize(vehicle.vehiculo?.Marca || '')} ${
            vehicle.vehiculo?.Modelo || ''
          }`,
          value: vehicle.vehiculo.Chasis,
        }));
        if (veh.length > 0) {
          setVehicles(veh);
          useSocket(veh);
          setSelectedVehicle(veh[0].label);
        }
      }
    })();
  }, []);

  useEffect(() => {
    loadScriptByURL(
      'recaptcha-key',
      `https://www.google.com/recaptcha/api.js?render=${recaptchaKey}`,
      () => {},
    );
  }, []);

  useEffect(() => {
    const initialVehicleSelected = Object.values(position).filter(
      (item) => item.label === selectedVehicle,
    );
    setSelectedVehicleData(initialVehicleSelected);
  }, [position]);

  const handleChange = (event) => {
    const selected = event.target.value;
    const vehicleSelected = Object.values(position).filter(
      (item) => item.label === selected,
    );
    setSelectedVehicleData(vehicleSelected);
    setSelectedVehicle(selected);
  };

 

  const userLogout = async () => {
    await logout();
  };

  const sharePosition = async () => {
    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(recaptchaKey, { action: 'submit' })
        .then(async (token) => {
          const { url } = await getUrlService(token, selectedVehicleData[0]);
          if (url) {
            setUrlShare(url);
          }
        });
    });
  };

  return (
    <MapPage
      view={view}
      setView={setView}
      urlShare={urlShare}
      position={position}
      vehicles={vehicles}
      userLogout={userLogout}
      handleChange={handleChange}
      sharePosition={sharePosition}
      selectedVehicle={selectedVehicle}
      selectedVehicleData={selectedVehicleData}
      userLocation={showUserLocation ? userLocation : null}
      showUserLocation={showUserLocation}
      locationError={showUserLocation ? locationError : null}
      plan={plan}
      notificationPermission={notificationPermission}
      enableNotifications={enableNotifications}
      notificationHistory={notificationHistory}
      unreadCount={unreadCount}
      notificationsReadAt={notificationsReadAt}
      refreshHistory={refreshHistory}
      markAsRead={markAsRead}
    />
  );
}

export default MapContainer;
